import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, CircularProgress, Alert, Autocomplete, TextField,
} from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import { screenService } from '../services/screenService';
import { wmsPersonnelPermissionService, type WmsScreenType } from '../services/wmsPersonnelPermissionService';
import { getPersonnel as getPersonnelSql } from '../../../services/migration/personnelDataService';
export interface AssignablePersonnel {
  id: string;
  name: string;
  email?: string;
  cashierId?: number;
}

interface AssignWmsPersonnelModalProps {
  open: boolean;
  title?: string;
  subtitle?: string;
  warehouseId: string;
  screenType: WmsScreenType;
  currentPersonnelId?: string;
  currentPersonnelName?: string;
  saving?: boolean;
  externalError?: string;
  showUnassign?: boolean;
  onClose: () => void;
  onSubmit: (personnel: AssignablePersonnel) => Promise<void> | void;
  onUnassign?: () => Promise<void> | void;
  emptyMessage?: string;
}

const AssignWmsPersonnelModal: React.FC<AssignWmsPersonnelModalProps> = ({
  open,
  title = 'Görevli Ata',
  subtitle,
  warehouseId,
  screenType,
  currentPersonnelId,
  currentPersonnelName,
  saving = false,
  externalError,
  showUnassign = false,
  onClose,
  onSubmit,
  onUnassign,
  emptyMessage,
}) => {
  const { userDetails } = useAuth();
  const [options, setOptions] = useState<AssignablePersonnel[]>([]);
  const [selectedPersonnelId, setSelectedPersonnelId] = useState(currentPersonnelId || '');
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');

  const selectedOption = useMemo(
    () => options.find((p) => p.id === selectedPersonnelId) || null,
    [options, selectedPersonnelId]
  );

  useEffect(() => {
    if (!open) return;

    const loadOptions = async () => {
      setLoadingOptions(true);
      setError('');
      setInfoMessage('');
      try {
        if (!userDetails?.companyId) {
          setOptions([]);
          setInfoMessage('Şirket bilgisi bulunamadı.');
          return;
        }

        if (!warehouseId) {
          setOptions([]);
          setInfoMessage('Depo seçilmedi.');
          return;
        }

        // Personel listesi SADECE SQL'den (kanonik). cashierId varsa SQL projeksiyonundan gelir.
        const sqlPersonnel = await getPersonnelSql(userDetails.companyId);
        const basePersonnel: AssignablePersonnel[] = (sqlPersonnel || [])
          .map((raw: any) => {
            const fromSql = raw?.cashierId ?? raw?.CashierId;
            const cashierId =
              typeof fromSql === 'number' ? fromSql : fromSql ? Number(fromSql) || undefined : undefined;
            return {
              id: raw.id,
              name: raw?.name || 'İsimsiz Personel',
              email: raw?.email,
              cashierId,
            } as AssignablePersonnel;
          })
          .filter((item): item is AssignablePersonnel => !!item && !!item.id && !!item.name);

        const screensResp = await screenService.getScreens(screenType, warehouseId);
        const screens = screensResp.data?.data || screensResp.data || [];
        const screenIds: string[] = screens.map((s: any) => s.id || s.Id).filter(Boolean);

        if (screenIds.length === 0) {
          setOptions([]);
          setInfoMessage(
            emptyMessage ||
              'Bu depoda henüz ekran tanımlanmamış. Önce ilgili sayfadan ekran ekleyip personel atayın.'
          );
          return;
        }

        const checks = await Promise.all(
          basePersonnel.map(async (personnel) => {
            const allowedScreens = await wmsPersonnelPermissionService.getMyWmsScreens(
              userDetails.companyId as string,
              personnel.id
            );
            const hasAccess = allowedScreens.some((screenId) => screenIds.includes(screenId));
            return hasAccess ? personnel : null;
          })
        );
        const filtered = checks.filter((item): item is AssignablePersonnel => !!item);
        setOptions(filtered);

        if (filtered.length === 0) {
          setInfoMessage(
            'Bu depodaki ekranlara atanmış personel bulunamadı. Önce ilgili sayfadan ekrana personel ekleyin.'
          );
        }
      } catch (err: any) {
        console.error('[AssignWmsPersonnelModal] Personel listesi yüklenemedi:', err);
        setError(err?.response?.data?.message || 'Personel listesi yüklenemedi.');
        setOptions([]);
      } finally {
        setLoadingOptions(false);
      }
    };

    loadOptions();
  }, [open, userDetails?.companyId, warehouseId, screenType, emptyMessage]);

  useEffect(() => {
    if (!open) return;
    setSelectedPersonnelId(currentPersonnelId || '');
    setError('');
  }, [open, currentPersonnelId]);

  const handleAssign = async () => {
    if (!selectedOption) {
      setError('Personel seçimi zorunludur.');
      return;
    }
    setError('');
    await onSubmit(selectedOption);
  };

  const handleUnassign = async () => {
    if (!onUnassign) return;
    setError('');
    await onUnassign();
  };

  const handleClose = () => {
    setSelectedPersonnelId('');
    setError('');
    onClose();
  };

  const displayedError = externalError || error;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}

          {currentPersonnelName && (
            <Alert severity="info" sx={{ py: 0 }}>
              Mevcut Görevli: <strong>{currentPersonnelName}</strong>
            </Alert>
          )}

          {infoMessage && <Alert severity="warning">{infoMessage}</Alert>}

          <Autocomplete
            options={options}
            value={selectedOption}
            loading={loadingOptions}
            getOptionLabel={(option) => option.name}
            onChange={(_event, value) => setSelectedPersonnelId(value?.id || '')}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Görevli *"
                placeholder="Personel seçin"
                autoFocus
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingOptions ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />

          {displayedError && (
            <Typography color="error" variant="body2">
              {displayedError}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {showUnassign && currentPersonnelName && (
          <Button color="warning" onClick={handleUnassign} disabled={saving}>
            Kaldır
          </Button>
        )}
        <Button onClick={handleClose} disabled={saving}>
          İptal
        </Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={saving || !selectedPersonnelId || loadingOptions}
        >
          {saving ? <CircularProgress size={18} /> : 'Ata'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignWmsPersonnelModal;
